import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Panel } from "@/components/ui/Panel";
import { Metric } from "@/components/ui/Metric";

type CheckStatus = "ok" | "warn" | "err" | "info";
interface Check {
  id: string;
  label: string;
  status: CheckStatus;
  value: string;
  description: string;
  action?: { href: string; label: string };
}
interface Section {
  id: string;
  title: string;
  checks: Check[];
}
interface HealthResponse {
  sections: Section[];
  counts: { ok: number; warn: number; err: number; info: number };
  fxWarnings: string[];
}

export const Route = createFileRoute("/admin/health")({
  component: HealthPage,
});

const TONE: Record<CheckStatus, { fg: string; bg: string; border: string; symbol: string }> = {
  ok: { fg: "var(--accent-2)", bg: "var(--accent-2-soft)", border: "color-mix(in srgb, var(--accent-2) 22%, transparent)", symbol: "✓" },
  warn: { fg: "var(--warn)", bg: "var(--warn-soft)", border: "color-mix(in srgb, var(--warn) 26%, transparent)", symbol: "⚠" },
  err: { fg: "var(--err)", bg: "var(--err-soft)", border: "color-mix(in srgb, var(--err) 30%, transparent)", symbol: "!" },
  info: { fg: "var(--muted)", bg: "var(--gray50)", border: "var(--hairline)", symbol: "·" },
};

function HealthPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "health"],
    queryFn: () => api.get<HealthResponse>("/api/admin/health"),
  });

  if (isLoading) return <p className="lede">Running checks…</p>;
  if (error || !data) {
    return (
      <Panel variant="err">
        <h2>Health check unavailable</h2>
        <p>{error instanceof Error ? error.message : "Failed to load."}</p>
      </Panel>
    );
  }

  return (
    <>
      <h1>System health</h1>

      <div className="grid cols-4">
        <Metric label="OK" value={String(data.counts.ok)} tone="accent" />
        <Metric label="Warnings" value={String(data.counts.warn)} tone={data.counts.warn > 0 ? "default" : "default"} />
        <Metric label="Errors" value={String(data.counts.err)} tone={data.counts.err > 0 ? "err" : "default"} />
        <Metric label="Info" value={String(data.counts.info)} />
      </div>

      {data.fxWarnings.length > 0 && (
        <Panel variant="warn">
          <strong>⚠ FX rates missing for:</strong> {data.fxWarnings.join(", ")} — health totals may be understated.
        </Panel>
      )}

      {data.sections.map((section) => (
        <Panel key={section.id} title={section.title} padding="tight">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 1, background: "var(--hairline)" }}>
            {section.checks.map((check) => {
              const tone = TONE[check.status];
              return (
                <div key={check.id} style={{ background: "var(--surface)", padding: "16px 18px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{check.label}</div>
                      <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 6, lineHeight: 1.45 }}>
                        {check.description}
                      </div>
                    </div>
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                        padding: "3px 9px",
                        borderRadius: 999,
                        background: tone.bg,
                        color: tone.fg,
                        border: `1px solid ${tone.border}`,
                        fontSize: 11,
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                      }}
                    >
                      <span aria-hidden>{tone.symbol}</span>
                      {check.value}
                    </span>
                  </div>
                  {check.action && (
                    <div style={{ marginTop: 10 }}>
                      <a href={check.action.href} style={{ fontSize: 12 }}>
                        {check.action.label}
                      </a>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Panel>
      ))}
    </>
  );
}
